import { wallClockToFormFields } from "@/lib/business-time";

export function todayISODate(timeZone?: string): string {
  const now = new Date();
  return timeZone ? wallClockToFormFields(now.toISOString(), timeZone).date : toISODate(now);
}

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseISODate(value: string | null | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00`);
  return toISODate(date) === value ? date : null;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const MAX_MONTHS_AHEAD = 3;

export function getMaxDate(timeZone?: string): Date {
  const d = timeZone ? new Date(`${todayISODate(timeZone)}T00:00:00`) : new Date();
  d.setMonth(d.getMonth() + MAX_MONTHS_AHEAD);
  return d;
}

export function isBeyondMaxDate(d: Date): boolean {
  return d > getMaxDate();
}

export interface DayInfo {
  iso: string;
  weekday: string;
  dayNum: number;
}

export function getWeekDays(weekOffset: number, timeZone?: string): DayInfo[] {
  const today = new Date(`${todayISODate(timeZone)}T00:00:00`);
  const start = new Date(today);
  start.setDate(today.getDate() + weekOffset * 7);

  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const days: DayInfo[] = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push({
      iso: toISODate(d),
      weekday: weekdays[d.getDay()] ?? "???",
      dayNum: d.getDate(),
    });
  }

  return days;
}

/** 24-hour clock (HH:mm). Use for all user-visible times — never 12h / AM·PM. */
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", TIME_FORMAT);
}

export function formatTimeFromDate(date: Date): string {
  return date.toLocaleTimeString("en-GB", TIME_FORMAT);
}

export function formatTimeRange(start: Date, durationMinutes: number): string {
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  return `${formatTimeFromDate(start)} – ${formatTimeFromDate(end)}`;
}

export function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

export function getWeekLabel(days: DayInfo[]): string {
  if (days.length === 0) return "";
  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  if (!firstDay || !lastDay) return "";
  const first = new Date(`${firstDay.iso}T00:00:00`);
  const last = new Date(`${lastDay.iso}T00:00:00`);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `${first.toLocaleDateString("en-US", opts)} – ${last.toLocaleDateString("en-US", opts)}`;
}
